import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Smile, Meh, Frown, Zap, Heart, Award, AlertCircle, Plus, X } from 'lucide-react';

const BehaviorTracker = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    child_mood: 'happy',
    behavior_events: [],
    communication_attempts: 0,
    triggers: [],
    wins: [],
    notes: ''
  });
  const [todayLog, setTodayLog] = useState(null);
  
  // New state for behavior tracking
  const [newTrigger, setNewTrigger] = useState('');
  const [newWin, setNewWin] = useState('');

  const moods = [
    { value: 'happy', label: 'Happy', icon: Smile, color: 'text-green-500' },
    { value: 'calm', label: 'Calm', icon: Heart, color: 'text-blue-500' },
    { value: 'anxious', label: 'Anxious', icon: AlertCircle, color: 'text-yellow-500' },
    { value: 'upset', label: 'Upset', icon: Frown, color: 'text-red-500' },
    { value: 'excited', label: 'Excited', icon: Zap, color: 'text-purple-500' }
  ];

  const behaviorEvents = [
    'meltdown', 'calm', 'social_win', 'breakthrough', 'tantrum', 'cooperative', 'withdrawn', 'engaged'
  ];

  useEffect(() => {
    loadChildren();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      loadTodayLog();
    }
  }, [selectedChild]);

  const loadChildren = async () => {
    try {
      const response = await API.get('/children');
      setChildren(response.data);
      if (response.data.length > 0 && !selectedChild) {
        setSelectedChild(response.data[0].child_id);
      }
    } catch (error) {
      console.error('Failed to load children:', error);
    }
  };

  const loadTodayLog = async () => {
    try {
      const response = await API.get(`/logs/today?child_id=${selectedChild}`);
      if (response.data) {
        setTodayLog(response.data);
        setFormData({
          child_mood: response.data.child_mood,
          behavior_events: response.data.behavior_events,
          communication_attempts: response.data.communication_attempts,
          triggers: response.data.triggers,
          wins: response.data.wins,
          notes: response.data.notes
        });
      } else {
        // Reset form for new log
        setTodayLog(null);
        setFormData({
          child_mood: 'happy',
          behavior_events: [],
          communication_attempts: 0,
          triggers: [],
          wins: [],
          notes: ''
        });
      }
    } catch (error) {
      console.error('No log for today:', error);
      setTodayLog(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedChild) {
      toast.error('Please select a child');
      return;
    }

    setLoading(true);
    try {
      await API.post('/logs', {
        child_id: selectedChild,
        ...formData,
        // Set default parent wellbeing values since we're not tracking them anymore
        parent_stress: 5,
        parent_energy: 5,
        parent_emotion: 'neutral',
        parent_confidence: 5
      });
      toast.success(todayLog ? 'Log updated!' : 'Log saved!');
      // Redirect to dashboard after save
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to save log');
      setLoading(false);
    }
  };

  const toggleBehaviorEvent = (event) => {
    setFormData(prev => ({
      ...prev,
      behavior_events: prev.behavior_events.includes(event)
        ? prev.behavior_events.filter(e => e !== event)
        : [...prev.behavior_events, event]
    }));
  };

  const addTrigger = () => {
    if (newTrigger.trim()) {
      setFormData(prev => ({
        ...prev,
        triggers: [...prev.triggers, newTrigger.trim()]
      }));
      setNewTrigger('');
    }
  };

  const addWin = () => {
    if (newWin.trim()) {
      setFormData(prev => ({
        ...prev,
        wins: [...prev.wins, newWin.trim()]
      }));
      setNewWin('');
    }
  };

  const removeItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  if (children.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">No children added yet</p>
        <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-4xl font-bold mb-2">Daily Behavior Tracker</h1>
        <p className="text-muted-foreground">Track your child's behaviors, moods, and patterns</p>
      </div>

      {/* Child Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Select Child</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedChild} onValueChange={setSelectedChild}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {children.map(child => (
                <SelectItem key={child.child_id} value={child.child_id}>
                  {child.name} ({child.age} years old)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Child Mood */}
        <Card>
          <CardHeader>
            <CardTitle>How was {children.find(c => c.child_id === selectedChild)?.name}'s mood today?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {moods.map(mood => {
                const Icon = mood.icon;
                const isSelected = formData.child_mood === mood.value;
                return (
                  <button
                    key={mood.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, child_mood: mood.value })}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/10 scale-105'
                        : 'border-gray-200 hover:border-gray-300 hover:scale-102'
                    }`}
                  >
                    <Icon className={`w-8 h-8 mx-auto mb-2 ${mood.color}`} />
                    <p className="text-sm font-medium">{mood.label}</p>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Behavior Events */}
        <Card>
          <CardHeader>
            <CardTitle>Behavior Events</CardTitle>
            <CardDescription>Select all behaviors observed today</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {behaviorEvents.map(event => (
                <Badge
                  key={event}
                  variant={formData.behavior_events.includes(event) ? 'default' : 'outline'}
                  className="cursor-pointer px-4 py-2 text-sm"
                  onClick={() => toggleBehaviorEvent(event)}
                >
                  {event.replace('_', ' ')}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Communication */}
        <Card>
          <CardHeader>
            <CardTitle>Communication Attempts</CardTitle>
            <CardDescription>Number of times your child attempted to communicate today</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setFormData({ ...formData, communication_attempts: Math.max(0, formData.communication_attempts - 1) })}
              >
                -
              </Button>
              <div className="flex-1 text-center">
                <p className="text-4xl font-bold text-primary">{formData.communication_attempts}</p>
                <p className="text-sm text-muted-foreground">attempts</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setFormData({ ...formData, communication_attempts: formData.communication_attempts + 1 })}
              >
                +
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Triggers & Wins */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Triggers</CardTitle>
              <CardDescription>What caused difficulties today?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.triggers.map((trigger, index) => (
                  <Badge key={index} variant="destructive" className="pl-3 pr-2 py-1">
                    {trigger}
                    <button
                      type="button"
                      onClick={() => removeItem('triggers', index)}
                      className="ml-2 hover:bg-red-600 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a trigger"
                  value={newTrigger}
                  onChange={(e) => setNewTrigger(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTrigger();
                    }
                  }}
                />
                <Button type="button" variant="outline" size="icon" onClick={addTrigger}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Wins</CardTitle>
              <CardDescription>What went well today?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.wins.map((win, index) => (
                  <Badge key={index} className="bg-green-600 pl-3 pr-2 py-1">
                    {win}
                    <button
                      type="button"
                      onClick={() => removeItem('wins', index)}
                      className="ml-2 hover:bg-green-700 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a win"
                  value={newWin}
                  onChange={(e) => setNewWin(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addWin();
                    }
                  }}
                />
                <Button type="button" variant="outline" size="icon" onClick={addWin}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle>Additional Notes</CardTitle>
            <CardDescription>Any other observations or details about today</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Any additional observations or notes about your child's day..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
            />
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button type="submit" size="lg" className="flex-1" disabled={loading}>
            {loading ? 'Saving...' : todayLog ? 'Update Log' : 'Save Log'}
          </Button>
          <Button type="button" size="lg" variant="outline" onClick={() => navigate('/dashboard')}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BehaviorTracker;
